import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AfyanexLogo } from "@/components/AfyanexLogo";
import ReportFullView from "@/components/ReportFullView";
import {
  Loader2,
  AlertTriangle,
  ShieldCheck,
  Download,
  Sparkles,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE}/share/reports/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) =>
        setError(err?.response?.data?.detail || "Link not found or expired")
      )
      .finally(() => setLoading(false));
  }, [token]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/share/reports/${token}/pdf`);
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `afyanex-report-${(data?.report?.id || "").slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-clinical-bg">
      {/* Header */}
      <header className="bg-white border-b border-clinical-border">
        <div className="mx-auto max-w-4xl px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AfyanexLogo markSize={26} />
            <span className="ml-1 pl-2.5 border-l border-clinical-border text-[10px] tracking-[0.14em] uppercase text-clinical-subtext font-semibold">
              Shared report
            </span>
          </div>
          {data && (
            <button
              type="button"
              onClick={download}
              disabled={downloading}
              className="btn-primary"
              data-testid="shared-download-pdf"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Preparing…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" /> Download PDF
                </>
              )}
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-32 text-clinical-subtext">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading shared report…
          </div>
        ) : error ? (
          <div
            className="card-surface p-10 text-center"
            data-testid="shared-error"
          >
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
              <AlertTriangle className="h-6 w-6" />
            </span>
            <h2 className="mt-4 font-heading text-xl font-semibold">
              {error}
            </h2>
            <p className="mt-2 text-sm text-clinical-subtext">
              Please ask the sender to issue a new link.
            </p>
          </div>
        ) : data ? (
          <>
            {/* Share metadata banner */}
            <div className="card-surface p-4" data-testid="shared-meta">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-clinical-primary/10 text-clinical-primary">
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    Shared with{" "}
                    <span className="font-medium text-clinical-text">
                      {data.share?.recipient_name || data.share?.recipient_kind}
                    </span>{" "}
                    by {data.share?.created_by_name}
                  </div>
                  <div className="mt-0.5 text-[11px] text-clinical-subtext">
                    Sent {new Date(data.share?.created_at).toLocaleString()} · expires{" "}
                    {new Date(data.share?.expires_at).toLocaleString()}
                  </div>
                  {data.share?.note && (
                    <div className="mt-2 rounded-lg border border-clinical-border bg-clinical-bg px-3 py-2 text-xs text-clinical-text">
                      <span className="font-medium">Note from sender: </span>
                      {data.share.note}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <ReportFullView data={data} />

            <footer className="text-center text-xs text-clinical-subtext pt-4 pb-2">
              <div className="inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Notes are professionally formatted by Afyanex AI before sharing.
              </div>
              <div className="mt-1">
                This is a private, view-only link. Please do not forward or distribute.
              </div>
              <a
                href="https://www.afyanex.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-clinical-border bg-white px-3 py-1.5 text-[11px] text-clinical-subtext transition-colors hover:border-afyanex-blue/40 hover:text-clinical-text"
              >
                <span>Powered by</span>
                <AfyanexLogo markSize={14} />
              </a>
            </footer>
          </>
        ) : null}
      </main>
    </div>
  );
}
